import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { JobRoleFields, type JobRoleValue } from './JobRoleFields';

interface JoinProfileStepProps {
  firstName: string;
  lastName: string;
  email: string;
  value: JobRoleValue;
  onChange: (patch: Partial<JobRoleValue>) => void;
}

/**
 * Flow B (joiner) profile step — personal profiling only (job function + level).
 *
 * The tenant already exists, so the joiner sees no Company / Modules / Demo /
 * tenant-classification steps: just a read-only identity confirmation and the
 * two role fields. These are the only profile fields the join-admit path
 * (`joinTenant`) persists. See `onboarding-unification-design.md` § "Flow B".
 */
export function JoinProfileStep({ firstName, lastName, email, value, onChange }: JoinProfileStepProps) {
  const { t } = useTranslation(['user_management', 'common']);

  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';

  return (
    <div className="space-y-6">
      {/* Read-only identity confirmation */}
      <div className="flex flex-col items-center gap-3 text-center">
        <Avatar className="h-16 w-16">
          <AvatarFallback className="text-lg">{initials}</AvatarFallback>
        </Avatar>
        <div>
          <p className="text-base font-semibold text-foreground">
            {`${firstName} ${lastName}`.trim() || email}
          </p>
          <p className="text-sm text-muted-foreground">{email}</p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">{t('setupWizardV2.join.profileHint')}</p>

      <JobRoleFields value={value} onChange={onChange} />
    </div>
  );
}
